import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BRAND } from '../branding/brandAssets';
import {
  canAccessImsPlatform,
  canAccessVmsPlatform,
  getImsDefaultLandingPath,
  getVmsDefaultLandingPath,
} from '../utils/vmsRole';
import { getPlatformSwitchPath } from '../utils/platformSwitch';

const SystemSelection: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    if (!canAccessVmsPlatform(user)) {
      sessionStorage.setItem('selectedSystem', 'ims');
      navigate(getImsDefaultLandingPath(user), { replace: true });
    } else if (!canAccessImsPlatform(user)) {
      sessionStorage.setItem('selectedSystem', 'vms');
      navigate(getVmsDefaultLandingPath(user), { replace: true });
    }
  }, [user, navigate]);

  const handleSystemSelect = (system: 'ims' | 'vms') => {
    navigate(getPlatformSwitchPath(system, user), { replace: true });
  };

  if (!user || (!canAccessImsPlatform(user) && !canAccessVmsPlatform(user))) {
    return null;
  }

  const showIms = canAccessImsPlatform(user);
  const showVms = canAccessVmsPlatform(user);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome, {user?.first_name} {user?.last_name}
          </h1>
          <p className="text-lg text-gray-600">
            {showIms && showVms ? 'Select IMS or VMS to continue' : 'Continue to your workspace'}
          </p>
        </div>

        <div className={`grid gap-6 ${showIms && showVms ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md mx-auto'}`}>
          {showIms ? (
          <button
            onClick={() => handleSystemSelect('ims')}
            className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-8 text-left border-2 border-transparent hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Select Invoice Management System"
          >
            <div className="flex items-center mb-4">
              <img
                src={BRAND.ims.icon72}
                alt=""
                className="h-12 w-12 mr-4 shrink-0 object-contain rounded-lg"
                width={48}
                height={48}
                aria-hidden
              />
              <h2 className="text-2xl font-bold text-gray-900">IMS</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Invoice Management System - Manage invoices, vendors, and financial approvals
            </p>
            <div className="text-primary-600 font-medium">Select IMS →</div>
          </button>
          ) : null}

          {showVms ? (
          <button
            onClick={() => handleSystemSelect('vms')}
            className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-8 text-left border-2 border-transparent hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Select Vendor Management System (VMS)"
          >
            <div className="flex items-center mb-4">
              <img
                src={BRAND.vms.icon72}
                alt=""
                className="h-12 w-12 mr-4 shrink-0 object-contain"
                width={48}
                height={48}
                aria-hidden
              />
              <h2 className="text-2xl font-bold text-gray-900">VMS</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Vendor Management (VMS) — vendor lifecycle, RFP, onboarding, and compliance
            </p>
            <div className="text-primary-600 font-medium">Select VMS →</div>
          </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SystemSelection;

